import type {
  MatchResult,
  RoommateCandidate,
  RoommatePreferences,
} from '../types/housing'

interface Dimension {
  key: keyof RoommatePreferences
  weight: number
  agreement: string
  difference: string
}

const DIMENSIONS: Dimension[] = [
  {
    key: 'sleep',
    weight: 20,
    agreement: 'Similar sleep schedule',
    difference: 'Different sleep schedule',
  },
  {
    key: 'tidiness',
    weight: 20,
    agreement: 'Similar cleanliness expectations',
    difference: 'Different cleanliness expectations',
  },
  {
    key: 'noise',
    weight: 16,
    agreement: 'Similar noise tolerance',
    difference: 'Different noise tolerance',
  },
  {
    key: 'study',
    weight: 10,
    agreement: 'Similar study habits',
    difference: 'Different study habits',
  },
  {
    key: 'smoking',
    weight: 12,
    agreement: 'Matching smoking preference',
    difference: 'Different smoking preference',
  },
  {
    key: 'guests',
    weight: 8,
    agreement: 'Similar approach to guests',
    difference: 'Different approach to guests',
  },
  {
    key: 'kind',
    weight: 6,
    agreement: 'Looking for the same accommodation type',
    difference: 'Looking at different accommodation types',
  },
  {
    key: 'roommateCount',
    weight: 8,
    agreement: 'Same preferred household size',
    difference: 'Different preferred household size',
  },
]

const BUDGET_WEIGHT = 20

/**
 * Preference-overlap score. This measures how closely two students' declared
 * preferences line up — it is not a prediction of how well they will live together.
 */
export function scoreCandidate(
  mine: RoommatePreferences,
  candidate: RoommateCandidate,
): MatchResult {
  const theirs = candidate.preferences
  const agreements: string[] = []
  const differences: string[] = []

  let earned = 0
  let possible = BUDGET_WEIGHT

  const spread = Math.abs(mine.budgetMax - theirs.budgetMax)
  const tolerance = Math.max(mine.budgetMax, theirs.budgetMax) * 0.25
  const budgetFit = tolerance === 0 ? 1 : Math.max(0, 1 - spread / tolerance)
  earned += budgetFit * BUDGET_WEIGHT
  if (budgetFit > 0.6) {
    agreements.push('Overlapping budget range')
  } else {
    differences.push('Budgets are far apart')
  }

  for (const dimension of DIMENSIONS) {
    possible += dimension.weight
    if (mine[dimension.key] === theirs[dimension.key]) {
      earned += dimension.weight
      agreements.push(dimension.agreement)
    } else {
      differences.push(dimension.difference)
    }
  }

  return {
    candidate,
    score: Math.round((earned / possible) * 100),
    agreements,
    differences,
  }
}

export function rankCandidates(
  mine: RoommatePreferences,
  candidates: RoommateCandidate[],
): MatchResult[] {
  return candidates
    .map((candidate) => scoreCandidate(mine, candidate))
    .sort((a, b) => b.score - a.score)
}
